import LFCAutocomplete from '_components/inputs/LFCAutocomplete';
import ProgressBar from '_components/ProgressBar';
import {
  CommitInfo,
  getSourceCollectors,
  getSourceArchive,
  getSourceCommits,
  getSourceContent,
  getSourceFiles,
  downloadFromUrl,
  SourceArchiveLimitExceededError,
  SourceFile
} from '_logics/LFCSourceUtil';
import GenericTemplate from '_templates/GenericTemplate';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import MapIcon from '@mui/icons-material/Map';
import {Alert, Box, Button, Tab, Tabs} from '@mui/material';
import LFCButton from '_components/inputs/LFCButton';

import FileHistoryDialog from './_components/FileHistoryDialog';
import Snapshots from './_components/Snapshots';
import FileTree from './_components/FileTree';
import FloorMapDialog from './_components/FloorMapDialog';
import SourceViewer from './_components/SourceViewer';
import SplitPane, {MAIN_AREA_HEIGHT} from './_components/SplitPane';
import AccumulationLogs from './_components/AccumulationLogs';

const PghistoryPage = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [view, setView] = useState<'git' | 'accum' | 'snapshot'>('git');

  // 選択状態
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<SourceFile | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [archiveDownloading, setArchiveDownloading] = useState(false);

  // マスタデータ
  const [deviceOptions, setDeviceOptions] = useState<{label: string}[]>([]);
  const [autoCompleteReset, setAutoCompleteReset] = useState(false);

  // ファイル一覧
  const [files, setFiles] = useState<SourceFile[]>([]);

  // 選択ファイルの変更履歴
  const [fileHistory, setFileHistory] = useState<CommitInfo[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [nextCommitId, setNextCommitId] = useState<string | null>(null);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  // ファイル履歴ダイアログ
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // フロアマップダイアログ
  const [floorMapOpen, setFloorMapOpen] = useState(false);

  // ファイル内容
  const [content, setContent] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isBinary, setIsBinary] = useState(false);
  const [latestCommitDate, setLatestCommitDate] = useState<string | null>(null);

  // ファイル内容取得のAbortController
  const contentAbortControllerRef = useRef<AbortController | null>(null);

  /**
   * 初期処理: collector一覧取得
   */
  useEffect(() => {
    const fetchCollectors = async () => {
      setStartProcess(true);
      try {
        const result = await getSourceCollectors();
        setDeviceOptions(result.map(name => ({label: name})));
      } catch (error) {
        console.error('端末一覧取得エラー:', error);
        enqueueSnackbar(t('端末一覧の取得に失敗しました'), {variant: 'error'});
      } finally {
        setStartProcess(false);
      }
    };

    fetchCollectors();
  }, []);

  /**
   * 端末選択時: ファイル一覧を取得
   */
  const handleDeviceChange = async (relayDatas: {name: string; data: string | string[]}) => {
    const deviceName = Array.isArray(relayDatas.data) ? relayDatas.data[0] : relayDatas.data;
    setSelectedDevice(deviceName);
    setSelectedFile(null);
    setSelectedPaths(new Set());
    setFiles([]);
    setContent(null);

    if (!deviceName) {
      return;
    }

    setStartProcess(true);
    try {
      const filesResult = await getSourceFiles(deviceName);
      setFiles(filesResult.files);

      if (filesResult.files.length === 0) {
        enqueueSnackbar(t('ファイルがありません'), {variant: 'info'});
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
      enqueueSnackbar(t('データの取得に失敗しました'), {variant: 'error'});
    } finally {
      setStartProcess(false);
    }
  };

  /**
   * ファイル選択（ツリーからファイルが選択された場合）
   */
  const handleFileSelect = async (file: SourceFile) => {
    // フォルダの場合はツリー内で展開されるので何もしない
    if (file.isFolder) {
      return;
    }

    // 前のリクエストをキャンセル
    if (contentAbortControllerRef.current) {
      contentAbortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    const abortController = new AbortController();
    contentAbortControllerRef.current = abortController;

    // ファイルの場合は内容を取得
    setSelectedFile(file);
    setContentLoading(true);
    setContent(null);
    setIsBinary(false);
    setFileHistory([]);
    setLatestCommitDate(null);

    try {
      // ファイル内容と最新コミット情報を並行取得
      const relativePath = file.path.replace(`devices/${selectedDevice}/`, '');
      const [contentResult, historyResult] = await Promise.all([
        getSourceContent(file.path, undefined, abortController.signal),
        getSourceCommits(selectedDevice, relativePath, 1).catch(() => ({
          commits: [],
          nextCommitId: null
        }))
      ]);

      // 最新コミット日時を設定
      if (historyResult.commits.length > 0) {
        setLatestCommitDate(historyResult.commits[0].date);
      }

      // large-response の場合
      if (contentResult.type === 'large-response') {
        // 大容量ファイルはダウンロードURLのみ表示
        setDownloadUrl(contentResult.url || null);
        setFileSize(contentResult.size);
        setContent(null);
        enqueueSnackbar(t('大容量ファイルのためダウンロードが必要です'), {variant: 'info'});
      } else {
        setContent(contentResult.content || '');
        setFileSize(contentResult.size);
        setIsBinary(contentResult.isBinary || false);
        setDownloadUrl(null);
      }
    } catch (error) {
      // キャンセルされた場合は何もしない
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      console.error('ファイル内容取得エラー:', error);
      enqueueSnackbar(t('ファイル内容の取得に失敗しました'), {variant: 'error'});
    } finally {
      // このリクエストがまだアクティブな場合のみローディングを解除
      if (contentAbortControllerRef.current === abortController) {
        setContentLoading(false);
      }
    }
  };

  const handleDownloadArchive = async () => {
    if (!selectedDevice || selectedPaths.size === 0) return;

    setArchiveDownloading(true);
    try {
      const archive = await getSourceArchive(selectedDevice, [...selectedPaths]);
      downloadFromUrl(archive.url, `${selectedDevice}_sources.zip`);
      if (archive.skipped.length > 0) {
        enqueueSnackbar(
          t('{{count}}件のファイルはダウンロード対象から除外されました', {
            count: archive.skipped.length
          }),
          {variant: 'warning'}
        );
      } else {
        enqueueSnackbar(
          t('{{count}}件のファイルをZIPでダウンロードします', {count: archive.fileCount}),
          {variant: 'success'}
        );
      }
    } catch (error) {
      console.error('ZIPダウンロードエラー:', error);
      enqueueSnackbar(
        error instanceof SourceArchiveLimitExceededError
          ? t('ZIPダウンロードの上限（500件または100MB）を超えています')
          : t('ZIPダウンロードの作成に失敗しました'),
        {variant: 'error'}
      );
    } finally {
      setArchiveDownloading(false);
    }
  };

  /**
   * 変更履歴を開く（Lazy loading）
   */
  const handleOpenHistory = async () => {
    if (!selectedFile) return;

    setHistoryDialogOpen(true);
    setFileHistory([]);
    setNextCommitId(null);
    setHistoryLoading(true);

    try {
      const relativePath = selectedFile.path.replace(`devices/${selectedDevice}/`, '');
      const historyResult = await getSourceCommits(selectedDevice, relativePath, 10);
      setFileHistory(historyResult.commits);
      setNextCommitId(historyResult.nextCommitId);
    } catch (error) {
      console.error('ファイル履歴取得エラー:', error);
      enqueueSnackbar(t('ファイル履歴の取得に失敗しました'), {variant: 'error'});
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * さらに履歴を読み込む
   */
  const handleLoadMoreHistory = async () => {
    if (!selectedFile || !nextCommitId) return;

    setLoadMoreLoading(true);

    try {
      const relativePath = selectedFile.path.replace(`devices/${selectedDevice}/`, '');
      const historyResult = await getSourceCommits(selectedDevice, relativePath, 10, nextCommitId);
      setFileHistory(prev => [...prev, ...historyResult.commits]);
      setNextCommitId(historyResult.nextCommitId);
    } catch (error) {
      console.error('ファイル履歴取得エラー:', error);
      enqueueSnackbar(t('追加の履歴取得に失敗しました'), {variant: 'error'});
    } finally {
      setLoadMoreLoading(false);
    }
  };

  return (
    <GenericTemplate title="Source Management">
      <ProgressBar startProcess={startProcess} />
      {/* 上部: 端末選択（全タブ共通） */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
        <LFCAutocomplete
          name="device"
          label={t('端末')}
          id="device"
          size="small"
          style={{minWidth: 400}}
          value={selectedDevice ? {label: selectedDevice} : null}
          onChange={handleDeviceChange}
          onReset={autoCompleteReset}
          doneReset={setAutoCompleteReset}
          multiple={false}
          selectItem={deviceOptions}
        />
        <LFCButton color="primary" onClick={() => setFloorMapOpen(true)}>
          <MapIcon fontSize="small" sx={{mr: 0.5}} />
          {t('マップ')}
        </LFCButton>
        <Box sx={{flex: 1}} />
      </Box>
      <Tabs
        value={view}
        variant="scrollable"
        scrollButtons="auto"
        sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}
        onChange={(_, value) => setView(value)}
        aria-label={t('accum.view')}
      >
        <Tab value="git" label={t('accum.gitHistory')} wrapped />
        <Tab value="accum" label={t('accum.title')} wrapped />
        <Tab value="snapshot" label={t('snapshot.title')} wrapped />
      </Tabs>
      {view === 'accum' && <AccumulationLogs selectedDevice={selectedDevice} />}
      {view === 'snapshot' && <Snapshots selectedDevice={selectedDevice} />}
      <Box sx={{display: view === 'git' ? 'block' : 'none'}}>
        {/* メインエリア */}
        <Box sx={{height: MAIN_AREA_HEIGHT, display: 'flex', flexDirection: 'column'}}>
          {!selectedDevice ? (
            <Alert severity="info">{t('端末を選択してください')}</Alert>
          ) : (
            <SplitPane
              left={
                <FileTree
                  files={files}
                  selectedPath={selectedFile?.path || null}
                  onSelect={handleFileSelect}
                  selectedPaths={selectedPaths}
                  onSelectedPathsChange={setSelectedPaths}
                  onDownloadArchive={handleDownloadArchive}
                  archiveDownloading={archiveDownloading}
                  deviceName={selectedDevice}
                />
              }
            >
              {/* ファイルビューア */}
              {isBinary ? (
                <Alert severity="info">
                  {t('バイナリファイルはプレビューできません')}
                  {downloadUrl && (
                    <Button href={downloadUrl} target="_blank" sx={{ml: 2}}>
                      {t('ダウンロード')}
                    </Button>
                  )}
                </Alert>
              ) : downloadUrl && !content ? (
                <Alert severity="info">
                  {t('大容量ファイルです')}
                  <Button href={downloadUrl} target="_blank" sx={{ml: 2}}>
                    {t('ダウンロード')}
                  </Button>
                </Alert>
              ) : (
                <SourceViewer
                  content={content}
                  fileName={selectedFile?.name || null}
                  filePath={selectedFile?.path || null}
                  fileSize={fileSize}
                  downloadUrl={downloadUrl}
                  loading={contentLoading}
                  hasHistory={selectedFile !== null}
                  onOpenHistory={handleOpenHistory}
                  commitDate={latestCommitDate}
                />
              )}
            </SplitPane>
          )}
        </Box>

        {/* ファイル変更履歴ダイアログ */}
      </Box>
      <FileHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        filePath={selectedFile?.path || ''}
        fileHistory={fileHistory}
        historyLoading={historyLoading}
        hasMore={nextCommitId !== null}
        onLoadMore={handleLoadMoreHistory}
        loadMoreLoading={loadMoreLoading}
      />

      {/* フロアマップダイアログ */}
      <FloorMapDialog
        open={floorMapOpen}
        onClose={() => setFloorMapOpen(false)}
        onSelectDevice={(deviceName: string) => {
          handleDeviceChange({name: 'device', data: deviceName});
        }}
      />
    </GenericTemplate>
  );
};

export default PghistoryPage;
